export type OtFechaCampo =
  | "fecha_cita"
  | "fecha_recepcion"
  | "fecha_estimada_entrega"
  | "fecha_terminacion"
  | "fecha_entrega"
  | "fecha_cierre";

export interface OtFechasCadena {
  fecha_alta?: string;
  fecha_cita?: string;
  fecha_recepcion?: string;
  fecha_estimada_entrega?: string;
  fecha_terminacion?: string;
  fecha_entrega?: string;
  fecha_cierre?: string;
}

const LABELS: Record<string, string> = {
  fecha_alta: "fecha de alta",
  fecha_cita: "fecha de cita",
  fecha_recepcion: "fecha de recepción de unidad",
  fecha_estimada_entrega: "fecha estimada de entrega",
  fecha_terminacion: "fecha de terminación de servicio",
  fecha_entrega: "fecha de entrega de unidad a cliente",
  fecha_cierre: "fecha de cierre",
};

const PREVIOUS_FIELD: Partial<Record<OtFechaCampo, keyof OtFechasCadena>> = {
  fecha_cita: "fecha_alta",
  fecha_recepcion: "fecha_cita",
  fecha_estimada_entrega: "fecha_recepcion",
  fecha_terminacion: "fecha_estimada_entrega",
  fecha_entrega: "fecha_terminacion",
  fecha_cierre: "fecha_entrega",
};

const MAX_GAP_DAYS: Partial<Record<OtFechaCampo, number>> = {
  fecha_cita: 60,
  fecha_recepcion: 14,
  fecha_estimada_entrega: 120,
  fecha_terminacion: 60,
  fecha_entrega: 7,
};

const MS_PER_DAY = 86_400_000;
const MONTH_TYPO_MIN_DAYS = 27;
const MONTH_TYPO_MAX_DAYS = 32;
const MAX_FUTURE_DAYS = 30;

function parseDateTime(value?: string | null): number | null {
  if (!value || value.trim() === "") return null;
  const normalized = value.trim().replace("T", " ").replace(/\.\d+$/, "");
  const withSeconds =
    normalized.length === 16 ? `${normalized}:00` : normalized;
  const ts = Date.parse(withSeconds.replace(" ", "T"));
  return Number.isNaN(ts) ? null : ts;
}

function startOfTodayTs(): number {
  const now = new Date();
  now.setHours(0, 0, 0, 0);
  return now.getTime();
}

function formatDisplay(value: string): string {
  const ts = parseDateTime(value);
  if (ts === null) return value;
  const d = new Date(ts);
  const pad = (n: number) => String(n).padStart(2, "0");
  return `${pad(d.getDate())}/${pad(d.getMonth() + 1)}/${d.getFullYear()} ${pad(d.getHours())}:${pad(d.getMinutes())}`;
}

export function toDateTimeLocalValue(value?: string | null): string {
  if (!value) return "";
  return value.replace(" ", "T").substring(0, 16);
}

export function toDateTimeLocalMin(ts: number): string {
  const d = new Date(ts);
  const pad = (n: number) => String(n).padStart(2, "0");
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
}

export function validateOtFecha(
  campo: OtFechaCampo,
  valor: string,
  cadena: OtFechasCadena,
): { valid: boolean; message: string } {
  const ts = parseDateTime(valor);
  if (ts === null) {
    return {
      valid: false,
      message: `Formato de fecha inválido para ${LABELS[campo] ?? campo}.`,
    };
  }

  if (ts < startOfTodayTs()) {
    return {
      valid: false,
      message: `No puede registrar fechas anteriores a hoy para ${LABELS[campo] ?? campo}.`,
    };
  }

  const prevField = PREVIOUS_FIELD[campo];
  if (prevField && cadena[prevField]) {
    const prevTs = parseDateTime(cadena[prevField]);
    if (prevTs !== null && ts <= prevTs) {
      return {
        valid: false,
        message: `La ${LABELS[campo]} debe ser posterior a la ${LABELS[prevField]} (${formatDisplay(cadena[prevField]!)}).`,
      };
    }
  }

  return { valid: true, message: "" };
}

function getMonthTypoHint(
  prevTs: number,
  currTs: number,
  diffDays: number,
): string | null {
  if (diffDays < MONTH_TYPO_MIN_DAYS || diffDays > MONTH_TYPO_MAX_DAYS) {
    return null;
  }

  const prev = new Date(prevTs);
  const curr = new Date(currTs);
  const sameDayAndTime =
    prev.getDate() === curr.getDate() &&
    prev.getHours() === curr.getHours() &&
    prev.getMinutes() === curr.getMinutes();

  if (sameDayAndTime) {
    return (
      "Las fechas tienen el mismo día y hora pero cambió el mes. " +
      "¿Podría ser un error de mes (por ejemplo, junio en lugar de julio)?"
    );
  }

  return (
    `Hay aproximadamente ${Math.round(diffDays)} días de diferencia (casi un mes). ` +
    "Revise que el mes y el día sean correctos."
  );
}

export function getOtFechaGapWarnings(
  campo: OtFechaCampo,
  valor: string,
  cadena: OtFechasCadena,
): string[] {
  const warnings: string[] = [];
  const ts = parseDateTime(valor);
  if (ts === null) return warnings;

  const prevField = PREVIOUS_FIELD[campo];
  if (prevField && cadena[prevField]) {
    const prevTs = parseDateTime(cadena[prevField]);
    if (prevTs !== null) {
      const diffDays = (ts - prevTs) / MS_PER_DAY;
      const maxDays = MAX_GAP_DAYS[campo] ?? 30;

      if (diffDays > maxDays) {
        warnings.push(
          `Hay ${Math.round(diffDays)} días entre la ${LABELS[prevField]} (${formatDisplay(cadena[prevField]!)}) ` +
            `y la ${LABELS[campo]} (${formatDisplay(valor)}). Revise si la fecha es correcta.`,
        );
      }

      const monthHint = getMonthTypoHint(prevTs, ts, diffDays);
      if (monthHint) warnings.push(monthHint);
    }
  }

  const daysFromNow = (ts - Date.now()) / MS_PER_DAY;
  if (daysFromNow > MAX_FUTURE_DAYS) {
    warnings.push(
      `La ${LABELS[campo]} está a más de ${MAX_FUTURE_DAYS} días en el futuro. ` +
        "Revise si capturó el mes o el día correctamente.",
    );
  }

  if (campo === "fecha_entrega" && cadena.fecha_estimada_entrega) {
    const estTs = parseDateTime(cadena.fecha_estimada_entrega);
    if (estTs !== null) {
      const diffEstDays = (ts - estTs) / MS_PER_DAY;
      if (diffEstDays > 14) {
        warnings.push(
          `La entrega es ${Math.round(diffEstDays)} días después de la fecha estimada (${formatDisplay(cadena.fecha_estimada_entrega)}). ` +
            "Confirme que es correcto.",
        );
      }
    }
  }

  return [...new Set(warnings)];
}

export function getMinDateTimeForField(
  campo: OtFechaCampo,
  cadena: OtFechasCadena,
): string {
  let minTs = startOfTodayTs();

  const prevField = PREVIOUS_FIELD[campo];
  if (prevField && cadena[prevField]) {
    const prevTs = parseDateTime(cadena[prevField]);
    if (prevTs !== null) {
      minTs = Math.max(minTs, prevTs + 60_000);
    }
  }

  return toDateTimeLocalMin(minTs);
}

export function buildOtFechasCadena(params: {
  fechaAlta?: string;
  fechaCita?: string;
  fechaRecepcion?: string;
  fechaEstimadaEntrega?: string;
  fechaTerminacion?: string;
  fechaEntrega?: string;
  fechaCierre?: string;
}): OtFechasCadena {
  return {
    fecha_alta: params.fechaAlta,
    fecha_cita: params.fechaCita,
    fecha_recepcion: params.fechaRecepcion,
    fecha_estimada_entrega: params.fechaEstimadaEntrega,
    fecha_terminacion: params.fechaTerminacion,
    fecha_entrega: params.fechaEntrega,
    fecha_cierre: params.fechaCierre,
  };
}

export function countEvidenciasCierre(
  evidenciasCierre: Record<number, { length?: number }[] | unknown[]>,
): number {
  return Object.values(evidenciasCierre).reduce(
    (acc, arr) => acc + (Array.isArray(arr) ? arr.length : 0),
    0,
  );
}
